/**
 * Copyright 2019, SumUp Ltd.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
/** @jsxRuntime classic */
/** @jsx jsx */
import React from 'react';
import { css, jsx } from '@emotion/core';
import { ChevronDown, ChevronUp } from '@sumup/icons';
import { uniqueId } from '../../util/id';
import deprecate from '../../util/deprecate';
import styled from '../../styles/styled';
import { textMega, hideVisually, inputOutline, } from '../../styles/style-mixins';
import useClickHandler from '../../hooks/use-click-handler';
import Label from '../Label';
import ValidationHint from '../ValidationHint';
const containerBaseStyles = ({ theme }) => css `
  label: select__container;
  color: ${theme.colors.n900};
  display: block;
  position: relative;
`;
const containerHideLabelStyles = ({ theme, hideLabel, }) => !hideLabel &&
    css `
    label: select__container--hide-label;

    label &,
    label + & {
      margin-top: ${theme.spacings.bit};
    }
  `;
const SelectContainer = styled('div')(containerBaseStyles, containerHideLabelStyles);
const labelMarginStyles = ({ theme, noMargin }) => {
    if (!noMargin) {
        deprecate([
            'The default outer spacing in the Select component is deprecated.',
            'Use the `noMargin` prop to silence this warning.',
            'Read more at https://github.com/sumup-oss/circuit-ui/issues/534.',
        ].join(' '));
        return css `
      label: input__label--margin;
      margin-bottom: ${theme.spacings.mega};
    `;
    }
    return null;
};
const labelInlineStyles = ({ inline }) => inline &&
    css `
    label: input__label--inline;
    display: inline-block;
  `;
const SelectLabel = styled(Label)(labelMarginStyles, labelInlineStyles);
const selectBaseStyles = ({ theme }) => css `
  label: select;
  appearance: none;
  cursor: pointer;
  background-color: ${theme.colors.white};
  outline: none;
  border: 0;
  border-radius: ${theme.borderRadius.tera};
  box-shadow: none;
  color: ${theme.colors.n900};
  margin: 0;
  padding-top: calc(${theme.spacings.byte} + 1px);
  padding-right: ${theme.spacings.tera};
  padding-bottom: calc(${theme.spacings.byte} + 1px);
  padding-left: ${theme.spacings.kilo};
  position: relative;
  width: 100%;
  z-index: ${theme.zIndex.select};
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  transition: box-shadow ${theme.transitions.default},
    padding ${theme.transitions.default};
  ${textMega({ theme })};

  &:-moz-focusring {
    color: transparent;
    text-shadow: 0 0 0 #000;
  }

  &::-ms-expand {
    display: none;
  }
`;
const selectInvalidStyles = ({ theme, invalid, disabled, }) => invalid &&
    !disabled &&
    css `
    label: select--invalid;
    padding-right: ${theme.spacings.zetta};
  `;
const selectPrefixStyles = ({ theme, hasPrefix }) => hasPrefix &&
    css `
    label: select--prefix;
    padding-left: ${theme.spacings.peta};
  `;
const SelectElement = styled.select(selectBaseStyles, selectInvalidStyles, selectPrefixStyles, inputOutline);
const labelTextStyles = ({ hideLabel }) => hideLabel && hideVisually();
const LabelText = styled('span')(labelTextStyles);
const optionalLabelStyles = ({ theme }) => css `
  label: input__optional-label;
  color: ${theme.colors.n700};
`;
const OptionalLabel = styled('span')(optionalLabelStyles);
/**
 * Used with css prop directly, so it does not require prop
 * destructuring.
 */
const prefixStyles = (theme) => css `
  label: select__prefix;
  display: block;
  position: absolute;
  top: 1px;
  left: 1px;
  z-index: ${theme.zIndex.select + 1};
  height: ${theme.spacings.peta};
  width: ${theme.spacings.peta};
  padding: ${theme.spacings.kilo};
  pointer-events: none;
`;
const iconBaseStyles = ({ theme }) => css `
  label: select__icon;
  color: ${theme.colors.n700};
  display: block;
  z-index: ${theme.zIndex.select + 1};
  pointer-events: none;
  position: absolute;
  height: ${theme.spacings.peta};
  width: ${theme.spacings.peta};
  top: 1px;
  right: 1px;
  padding: ${theme.spacings.kilo};
`;
const iconActiveStyles = () => css `
  label: select__icon-active;
  select:active ~ & {
    display: none;
  }
`;
const iconInactiveStyles = () => css `
  label: select__icon-inactive;
  select:not(:active) ~ & {
    display: none;
  }
`;
const IconActive = styled(ChevronDown)(iconBaseStyles, iconActiveStyles);
const IconInactive = styled(ChevronUp)(iconBaseStyles, iconInactiveStyles);
/**
 * A native select component.
 */
export const Select = React.forwardRef(({ value, defaultValue, placeholder = 'Select an option', disabled, noMargin, inline, invalid, required, options, children, renderPrefix: RenderPrefix, validationHint, optionalLabel, label, hideLabel, className, style, id: customId, onChange, tracking, ...props }, ref) => {
    const id = customId || uniqueId('select_');
    const prefix = RenderPrefix && (jsx(RenderPrefix, { css: prefixStyles, value: value }));
    const hasPrefix = Boolean(prefix);
    const handleChange = useClickHandler(onChange, tracking, 'select');
    if (!label) {
        deprecate([
            'The label is now built into the Select component.',
            'Use the `label` prop to pass in the label content and',
            'remove the Label component from your code.',
            'The label will become required in the next major version.',
        ].join(' '));
    }
    return (jsx(SelectLabel, { className: className, style: style, htmlFor: id, inline: inline, disabled: disabled, noMargin: noMargin, as: label ? 'label' : 'span' },
        label && (jsx(LabelText, { hideLabel: hideLabel },
            label,
            optionalLabel && !required ? (jsx(OptionalLabel, null, ` (${optionalLabel})`)) : null)),
        jsx(SelectContainer, { hideLabel: hideLabel },
            prefix,
            jsx(SelectElement, Object.assign({ id: id, value: value, ref: ref, invalid: invalid, "aria-invalid": invalid, required: required, disabled: disabled, hasPrefix: hasPrefix, defaultValue: defaultValue }, props, { onChange: handleChange }),
                !value && !defaultValue && (jsx("option", { key: "placeholder", value: "" }, placeholder)),
                children ||
                    (options &&
                        options.map(({ label: labelValue, ...rest }) => (jsx("option", Object.assign({ key: rest.value }, rest), labelValue))))),
            jsx(IconActive, null),
            jsx(IconInactive, null)),
        jsx(ValidationHint, { disabled: disabled, invalid: invalid, validationHint: validationHint })));
});
Select.displayName = 'Select';
